import React, { useState, useEffect } from 'react'
import {
  Download,
  Check,
  ExternalLink,
  Save,
  MessageCircle,
  Newspaper,
  FileText,
  ChevronUp,
  ChevronDown,
  Trash2,
  MousePointer,
  Clipboard,
} from 'lucide-react'
import { Layout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { SentimentChart } from '@/components/scan'
import { DeleteScanDialog } from '@/features/scan/components/DeleteScanDialog'
import { scanService } from '@/lib/services/scanService'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useNavigate } from '@tanstack/react-router'
import { cn, exportScanToWord } from '@/lib/utils'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { Scan, ScanSourceDetail } from '@/lib/types'
import { marked } from 'marked'
import TurndownService from 'turndown'
import { ScanInfoCard } from './components/ScanInfoCard'

// Configure marked for safe HTML output
marked.setOptions({
  breaks: true,
  gfm: true,
})

// Create turndown service for HTML to markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
})

// Utility functions for content conversion
const markdownToHtml = (markdown: string): string => {
  return marked.parse(markdown) as string
}

const htmlToMarkdown = (html: string): string => {
  return turndownService.turndown(html)
}

// Remove source references from markdown for clean editing
const cleanSourceReferences = (markdown: string): string => {
  return markdown.replace(/##\d+\$/g, '')
}

// Parse text into sentences with their source references
interface SentenceWithSources {
  text: string
  sourceIndices: number[]
  startPos: number
  endPos: number
}

// Extract domain from URL
const getDomainFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url)
    // Remove 'www.' prefix if present
    return urlObj.hostname.replace(/^www\./, '')
  } catch {
    return null
  }
}

const parseSentencesWithSources = (text: string): SentenceWithSources[] => {
  const sentences: SentenceWithSources[] = []

  // Split by newline first to get individual lines/sentences
  const lines = text.split('\n')

  for (const line of lines) {
    const trimmedLine = line.trim()

    if (trimmedLine.length === 0) continue

    // Extract source indices from this line
    const sourceIndices: number[] = []
    const sourceRegex = /##(\d+)\$/g
    let sourceMatch

    while ((sourceMatch = sourceRegex.exec(trimmedLine)) !== null) {
      sourceIndices.push(parseInt(sourceMatch[1], 10))
    }

    // Clean the line text (remove any source markers and asterisks)
    const cleanText = trimmedLine
      .replace(/##\d+\$/g, '')
      .replace(/\*/g, '')
      .trim()

    if (cleanText.length > 0) {
      sentences.push({
        text: cleanText,
        sourceIndices,
        startPos: 0,
        endPos: trimmedLine.length,
      })
    }
  }

  return sentences
}

export function ScanDetailPage() {
  // Derive id from the path as a fallback to avoid strict TanStack typing issues
  let id: string | undefined
  if (typeof window !== 'undefined') {
    const parts = window.location.pathname.split('/')
    id = parts[parts.length - 1] || undefined
  }
  const [scan, setScan] = useState<Scan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [blockContent, setBlockContent] = useState<Record<string, string>>({})
  const [originalBlockContent, setOriginalBlockContent] = useState<Record<string, string>>({})
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [confirmSuccess, setConfirmSuccess] = useState(false)
  const [isContentChecked, setIsContentChecked] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [manualSaveLoading, setManualSaveLoading] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [expandedSourceTypes, setExpandedSourceTypes] = useState<Set<string>>(new Set())
  const [showSourcesDialog, setShowSourcesDialog] = useState(false)
  const [selectedBlockForSources, setSelectedBlockForSources] = useState<Scan['blocks'][0] | null>(
    null
  )
  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set())
  const [activeSentenceIndex, setActiveSentenceIndex] = useState<number | null>(null)

  // Sentiment data for chart
  const sentimentColorClass: Record<string, string> = {
    'Positief': 'bg-green-500',
    'Neutraal': 'bg-gray-500',
    'Negatief': 'bg-red-500',
  }
  let sentimentData: Array<{ name: string; value: number; color: string }> = []

  const { activeCrisis } = useCrisisStore()
  const isClosedCrisis = activeCrisis?.status === 'closed'

  // Permission checks
  const canUpdateScans = useHasPermission('scans.update')
  const canDeleteScans = useHasPermission('scans.delete')

  const navigate = useNavigate()

  useEffect(() => {
    if (!id) {
      setError('No scan ID provided')
      setLoading(false)
      return
    }

    const fetchScan = async () => {
      try {
        const response = await scanService.getScan(id!)
        const scanData = response.data as Scan

        // Deny access to non-confirmed scans for users without scans.update permission
        if (scanData.status !== 'confirmed' && !canUpdateScans) {
          setError('U hebt geen toestemming om deze analyse te bekijken. Alleen bevestigde analyses zijn zichtbaar voor u.')
          setLoading(false)
          return
        }

        setScan(scanData)

        // Initialize block content from scan data
        const initialContent: Record<string, string> = {}
        const initialOriginalContent: Record<string, string> = {}

        // Ensure originalAiAnswers exists (fallback to empty object for old scans)
        const originalAnswers = scanData.originalAiAnswers || {}

        scanData.blocks.forEach(block => {
          // Store original content with source references for dialog from original_ai_answers
          // Map block IDs to original AI answer fields
          const fieldMapping: Record<string, string> = {
            block_1: 'samenvatting',
            block_2: 'v1_WatDoenMensen',
            block_3: 'v2_WelkeVragen',
            block_4: 'v3_HoeVoelen',
            block_5: 'v4_BeeldPers',
            block_6: 'notities',
          }
          const originalField = fieldMapping[block.id]

          // Try to get from original_ai_answers, fallback to current block content
          if (originalField && originalAnswers[originalField]) {
            initialOriginalContent[block.id] = originalAnswers[originalField]
          } else if (originalField) {
            // Fallback: use current block content if original_ai_answers is missing
            // This happens for scans created before source indexing was added
            initialOriginalContent[block.id] = block.content
          }

          // Convert clean markdown (without source references) to HTML for the rich text editor
          initialContent[block.id] = markdownToHtml(cleanSourceReferences(block.content))
        })
        setBlockContent(initialContent)
        setOriginalBlockContent(initialOriginalContent)
      } catch (err) {
        console.error('Error fetching scan:', err)
        setError('Failed to load scan data')
      } finally {
        setLoading(false)
      }
    }

    fetchScan()
  }, [id, canUpdateScans])

  // Create a debounced function for each block ID
  const debouncedSaveFunctions = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const handleContentChange = (blockId: string, content: string) => {
    // Prevent editing when scan is confirmed, deleted, crisis is closed, or user lacks update permission
    if (
      scan?.status === 'confirmed' ||
      scan?.status === 'deleted' ||
      isClosedCrisis ||
      !canUpdateScans
    ) {
      return
    }

    setBlockContent(prev => ({
      ...prev,
      [blockId]: content,
    }))

    // Auto-save after content change with debouncing
    // Clear any existing timeout for this blockId
    if (debouncedSaveFunctions.current[blockId]) {
      clearTimeout(debouncedSaveFunctions.current[blockId])
    }

    // Set a new timeout
    debouncedSaveFunctions.current[blockId] = setTimeout(() => {
      saveBlock(blockId, content)
    }, 1500) // 1.5 seconds debounce
  }

  const saveBlock = async (blockId: string, htmlContent: string) => {
    // Prevent saving when scan is confirmed, deleted, crisis is closed, or user lacks update permission
    if (
      scan?.status === 'confirmed' ||
      scan?.status === 'deleted' ||
      isClosedCrisis ||
      !canUpdateScans
    ) {
      return
    }

    // Convert HTML back to markdown for API storage
    const markdownContent = htmlToMarkdown(htmlContent)

    // Skip saving if content is empty or unchanged
    const block = scan?.blocks.find(b => b.id === blockId)
    if (!block || block.content === markdownContent) return

    setIsSaving(true)
    try {
      // Call the API to save the block content
      if (id) {
        await scanService.updateBlockContent(id, blockId, markdownContent)
      }

      // Update the scan object with new content
      if (scan) {
        const updatedBlocks = scan.blocks.map(block =>
          block.id === blockId ? { ...block, content: markdownContent } : block
        )
        setScan({ ...scan, blocks: updatedBlocks })
      }

      // Update last saved timestamp
      setLastSavedAt(new Date())
    } catch (error) {
      console.error('Error saving block:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const manualSaveAll = async () => {
    // Don't allow manual save when there's no scan, when scan is confirmed, deleted, when crisis is closed, or user lacks update permission
    if (
      !scan ||
      scan.status === 'confirmed' ||
      scan.status === 'deleted' ||
      isClosedCrisis ||
      !canUpdateScans
    )
      return

    setManualSaveLoading(true)
    setIsSaving(true)

    try {
      // Save all blocks that have changes
      const savePromises = scan.blocks
        .map(async block => {
          const htmlContent = blockContent[block.id]
          if (!htmlContent) return

          const markdownContent = htmlToMarkdown(htmlContent)
          if (block.content === markdownContent) return

          return scanService.updateBlockContent(id!, block.id, markdownContent)
        })
        .filter(Boolean)

      await Promise.all(savePromises)

      // Update all blocks in the scan object
      const updatedBlocks = scan.blocks.map(block => {
        const htmlContent = blockContent[block.id]
        if (!htmlContent) return block

        const markdownContent = htmlToMarkdown(htmlContent)
        return { ...block, content: markdownContent }
      })

      setScan({ ...scan, blocks: updatedBlocks })
      setLastSavedAt(new Date())

      // Show success state
      setSaveSuccess(true)
      setTimeout(() => {
        setSaveSuccess(false)
      }, 3000)
    } catch (error) {
      console.error('Error saving all blocks:', error)
    } finally {
      setManualSaveLoading(false)
      setIsSaving(false)
    }
  } // Format saving status text

  const getSavingStatusText = () => {
    if (isSaving) {
      return 'Opslaan...'
    }

    if (lastSavedAt) {
      const secondsAgo = Math.floor((Date.now() - lastSavedAt.getTime()) / 1000)
      if (secondsAgo < 60) {
        return `Automatisch opgeslagen ${secondsAgo} seconden geleden`
      } else {
        const minutesAgo = Math.floor(secondsAgo / 60)
        return `Automatisch opgeslagen ${minutesAgo} minuten geleden`
      }
    }

    return null
  }

  const confirmAnalysis = async () => {
    setIsConfirming(true)
    setConfirmError(null)
    setConfirmSuccess(false)
    try {
      // Disallow confirming when crisis is closed
      if (isClosedCrisis) {
        setConfirmError('Crisis is gesloten. Bevestigen is niet mogelijk.')
        setIsConfirming(false)
        return
      }

      if (id) {
        await scanService.confirmScan(id)

        // Update the scan status locally
        if (scan) {
          setScan({ ...scan, status: 'confirmed' })
        }

        setConfirmSuccess(true)
        // Close dialog after a brief delay to show success message
        setTimeout(() => {
          setShowConfirmDialog(false)
          setConfirmSuccess(false)
        }, 1500)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to confirm scan'
      setConfirmError(errorMessage)
      console.error('Error confirming analysis:', error)
    } finally {
      setIsConfirming(false)
    }
  }

  const exportToWord = async () => {
    if (!scan) return

    try {
      await exportScanToWord(scan, blockContent)
    } catch (error) {
      console.error('Error exporting to Word:', error)
      // Could add user notification here
    }
  }

  const handleDeleteClick = () => {
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async () => {
    if (!id) return

    setIsDeleting(true)
    try {
      await scanService.deleteScan(id)
      // Redirect to scan list
      await navigate({ to: '/scan' })
    } catch (err) {
      console.error('Error deleting scan:', err)
      setIsDeleting(false)
    } finally {
      setShowDeleteDialog(false)
    }
  }

  return (
    <Layout removeBottomPadding={true}>
      {loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading scan data...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </div>
      ) : scan ? (
        <>
          <div className="container mx-auto px-2 md:px-6">
            {/* Mobile Scan Info Card */}
            <ScanInfoCard scan={scan} className="block lg:hidden" />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="xl:col-span-2 space-y-8">
                {/* Analysis Blocks */}
                {scan.blocks.map(block => (
                  <Card key={block.id}>
                    <CardHeader>
                      <CardTitle className="text-lg">{block.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {scan?.status === 'confirmed' && (
                        <div className="mb-2 p-2 bg-muted/50 rounded-md border">
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <span>🔒</span>
                            Deze analyse is bevestigd en kan niet meer worden bewerkt
                          </p>
                        </div>
                      )}
                      {isClosedCrisis && scan.status !== 'deleted' && (
                        <div className="mb-2 p-2 bg-muted/50 rounded-md border">
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <span>🔒</span>
                            Deze crisis is gesloten — bewerken en opslaan zijn uitgeschakeld. U kunt
                            de analyse alleen bekijken en exporteren.
                          </p>
                        </div>
                      )}
                      {!canUpdateScans &&
                        scan.status !== 'confirmed' &&
                        scan.status !== 'deleted' &&
                        !isClosedCrisis && (
                          <div className="mb-2 p-2 bg-muted/50 rounded-md border">
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <span>🔒</span>U heeft geen toestemming om deze analyse te bewerken.
                            </p>
                          </div>
                        )}
                      <RichTextEditor
                        value={blockContent[block.id] || ''}
                        onChange={content => handleContentChange(block.id, content)}
                        className={`bg-background ${scan?.status === 'confirmed' || scan?.status === 'deleted' || isClosedCrisis || !canUpdateScans ? 'opacity-75' : ''}`}
                        disabled={
                          scan?.status === 'confirmed' ||
                          scan?.status === 'deleted' ||
                          isClosedCrisis ||
                          !canUpdateScans
                        }
                        headerRight={(() => {
                          // Get sources from scan object or fall back to block sources
                          let scanSources: ScanSourceDetail[] = []
                          if (scan.sources) {
                            if (Array.isArray(scan.sources)) {
                              scanSources = scan.sources
                            } else if (typeof scan.sources === 'object') {
                              scanSources = Object.entries(scan.sources).map(
                                ([key, value]: [string, ScanSourceDetail]) => ({
                                  id: key,
                                  ...value,
                                })
                              )
                            }
                          }
                          return scanSources && scanSources.length > 0 ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedBlockForSources(block)
                                setShowSourcesDialog(true)
                              }}
                            >
                              Bronnen
                            </Button>
                          ) : undefined
                        })()}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Sidebar - Charts */}
              <div className="space-y-6">
                <ScanInfoCard scan={scan} className="hidden xl:block" />
                {/* Sources Panel */}
                {(() => {
                  // Get sources from scan object or fall back to block sources
                  let scanSources: ScanSourceDetail[] = []
                  if (scan.sources) {
                    if (Array.isArray(scan.sources)) {
                      scanSources = scan.sources
                    } else if (typeof scan.sources === 'object') {
                      scanSources = Object.entries(scan.sources).map(
                        ([key, value]: [string, ScanSourceDetail]) => ({
                          id: key,
                          ...value,
                        })
                      )
                    }
                  } else {
                    scanSources = scan.blocks.flatMap(block => block.sources || [])
                  }

                  // Check if we have any sources to display
                  const hasSources = scanSources && scanSources.length > 0

                  // Group sources by type and calculate totals (unique count only)
                  const sourcesByType: Record<
                    string,
                    { sources: ScanSourceDetail[]; totalCount: number }
                  > = scanSources.reduce(
                    (acc, source) => {
                      const type = source.sourceType || 'other'
                      if (!acc[type]) {
                        acc[type] = { sources: [], totalCount: 0 }
                      }
                      acc[type].sources.push(source)
                      acc[type].totalCount += 1 // Count unique sources only, not duplicates
                      return acc
                    },
                    {} as Record<string, { sources: ScanSourceDetail[]; totalCount: number }>
                  )

                  // Calculate sentiment counts for chart
                  const sentimentCounts = scanSources.reduce(
                    (acc, source) => {
                      const sentiment = source.sentiment || 'neutral'
                      acc[sentiment] = (acc[sentiment] || 0) + 1
                      return acc
                    },
                    {} as Record<string, number>
                  )

                  sentimentData = [
                    { name: 'Positief', value: sentimentCounts.positive || 0, color: '#22c55e' },
                    { name: 'Neutraal', value: sentimentCounts.neutral || 0, color: '#6b7280' },
                    { name: 'Negatief', value: sentimentCounts.negative || 0, color: '#ef4444' },
                  ]

                  // Toggle expanded state for a source type
                  const toggleSourceType = (sourceType: string) => {
                    const newExpanded = new Set<string>()
                    // If the clicked type was already expanded, just close it
                    // Otherwise, close all and open only this one
                    if (!expandedSourceTypes.has(sourceType)) {
                      newExpanded.add(sourceType)
                    }
                    setExpandedSourceTypes(newExpanded)
                  }

                  // Get icon for source type
                  const getSourceTypeIcon = (sourceType: string) => {
                    switch (sourceType.toLowerCase()) {
                      case 'facebook':
                        return <MessageCircle className="w-4 h-4" />
                      case 'twitter':
                      case 'x':
                        return <MessageCircle className="w-4 h-4" />
                      case 'news':
                        return <Newspaper className="w-4 h-4" />
                      case 'reddit':
                        return <MessageCircle className="w-4 h-4" />
                      default:
                        return <FileText className="w-4 h-4" />
                    }
                  }

                  // Get display name for source type
                  const getSourceTypeName = (sourceType: string) => {
                    switch (sourceType.toLowerCase()) {
                      case 'facebook':
                        return 'Facebook'
                      case 'twitter':
                      case 'x':
                        return 'Twitter/X'
                      case 'news':
                        return 'Nieuws'
                      case 'reddit':
                        return 'Reddit'
                      default:
                        return sourceType.charAt(0).toUpperCase() + sourceType.slice(1)
                    }
                  }
                  return hasSources ? (
                    <Card className="mt-6 border-0">
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-base">Bronnen</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 pt-0">
                        {Object.entries(sourcesByType).map(([sourceType, typeData]) => {
                          const { sources, totalCount } = typeData
                          return (
                            <div key={sourceType} className="rounded-lg">
                              {/* Collapsible Header */}
                              <button
                                onClick={() => toggleSourceType(sourceType)}
                                className="w-full flex items-center justify-between p-3 hover:bg-accent/50 transition-colors text-left"
                              >
                                <div className="flex items-center gap-3">
                                  {getSourceTypeIcon(sourceType)}
                                  <span className="font-medium">
                                    {getSourceTypeName(sourceType)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">
                                    {totalCount} berichten
                                  </span>
                                  {expandedSourceTypes.has(sourceType) ? (
                                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </div>
                              </button>

                              {/* Expandable Content */}
                              {expandedSourceTypes.has(sourceType) && (
                                <div className="pb-3 border-t bg-muted/20">
                                  <div className="pt-3">
                                    {sources.map((source: ScanSourceDetail, index: number) => {
                                      // Calculate global index for this source
                                      const globalIndex = scanSources.findIndex(s => s === source)

                                      return (
                                        <div
                                          key={index}
                                          id={`source-${globalIndex}`}
                                          className="flex items-center justify-between py-2 px-3 bg-background rounded border-b"
                                        >
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">
                                              {(source.sourceType === 'news' || source.sourceType === 'blog')
                                                ? getDomainFromUrl(source.url || '') || source.name || source.title || 'Untitled'
                                                : source.title || source.author || source.name || 'Untitled'}
                                            </div>
                                            <div className='flex items-center gap-2'>
                                              {source.timestamp && (
                                                <div className="text-xs text-muted-foreground mt-1">
                                                  {new Date(source.timestamp).toLocaleString('nl-NL')}
                                                </div>
                                              )}
                                              {source.sentiment && (
                                                <span
                                                  className={`inline-block px-2 py-0.5 text-xs rounded mt-1 ${source.sentiment === 'positive'
                                                    ? 'bg-green-100 text-green-700'
                                                    : source.sentiment === 'negative'
                                                      ? 'bg-red-100 text-red-700'
                                                      : 'bg-gray-100 text-gray-700'
                                                    }`}
                                                >
                                                  {source.sentiment === 'positive'
                                                    ? 'Positief'
                                                    : source.sentiment === 'negative'
                                                      ? 'Negatief'
                                                      : source.sentiment === 'neutral'
                                                        ? 'Neutraal'
                                                        : source.sentiment}
                                                </span>
                                              )}
                                            </div>
                                            {source.text && (
                                              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                {source.text.length > 150
                                                  ? source.text.substring(0, 150) + '...'
                                                  : source.text}
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 ml-2">
                                            {source.count && source.count > 1 && (
                                              <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                                                {source.count}
                                              </span>
                                            )}
                                            {source.url && (
                                              <a
                                                href={source.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                                              >
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </CardContent>
                    </Card>
                  ) : null
                })()}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sentiment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6">
                      <div className="flex-shrink-0 w-48">
                        <SentimentChart data={sentimentData} />
                      </div>
                      <div className="flex-1 space-y-3">
                        {(() => {
                          const total = sentimentData.reduce((sum, item) => sum + item.value, 0)
                          return sentimentData.map(item => (
                            <div key={item.name} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div
                                  className={cn('w-3 h-3 rounded-full', sentimentColorClass[item.name])}
                                />
                                <span className="text-sm font-medium">{item.name}</span>
                              </div>
                              <span className="text-sm text-muted-foreground font-medium">
                                {total > 0 ? Math.round((item.value / total) * 100) : 0}%
                              </span>
                            </div>
                          ))
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-4 bg-white border-t border-border shadow-lg z-50 px-6 py-4 mt-8 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Manual Save Button */}
                {scan &&
                  scan.status !== 'confirmed' &&
                  scan.status !== 'deleted' &&
                  !isClosedCrisis &&
                  canUpdateScans && (
                    <Button
                      // variant="outline"
                      size="lg"
                      className="gap-2"
                      onClick={manualSaveAll}
                      loading={manualSaveLoading}
                    >
                      {saveSuccess ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                      Opslaan
                    </Button>
                  )}

                {/* Saving Status */}
                {getSavingStatusText() && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 rounded-full border-2 border-t-transparent border-primary animate-spin" />
                        <span>{getSavingStatusText()}</span>
                      </>
                    ) : (
                      <span>{getSavingStatusText()}</span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                {/* Export Button */}
                <Button variant="outline" className="gap-2" onClick={exportToWord}>
                  <Download className="w-4 h-4" />
                  Exporteren naar Word
                </Button>

                {/* Confirm Button (disabled for closed crises) */}
                {(scan?.status === 'ready' || scan?.status === 'draft') && !isClosedCrisis && (
                  <Button
                    variant="gradient"
                    className="gap-2 px-5"
                    onClick={() => {
                      setShowConfirmDialog(true)
                      setIsContentChecked(false) // Reset checkbox when opening dialog
                    }}
                  >
                    <Check className="w-4 h-4 -ml-1" />
                    <span>Bevestigen</span>
                  </Button>
                )}

                {/* Delete Button */}
                {scan && scan.status !== 'deleted' && !isClosedCrisis && canDeleteScans && (
                  <Button
                    variant="outline"
                    className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 hover:border-red-400"
                    onClick={handleDeleteClick}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Confirmation Dialog */}
          <Dialog
            open={showConfirmDialog}
            onOpenChange={(open: boolean) => {
              setShowConfirmDialog(open)
              if (!open) {
                setIsContentChecked(false) // Reset checkbox when dialog closes
              }
            }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Analyse bevestigen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {confirmSuccess ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-green-700 font-medium">Analyse succesvol bevestigd!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      De analyse is nu beschikbaar voor andere tools.
                    </p>
                  </div>
                ) : (
                  <>
                    {isClosedCrisis ? (
                      <div className="space-y-4">
                        <p className="text-muted-foreground">
                          Deze crisis is gesloten. Bevestigen is niet mogelijk.
                        </p>
                        <div className="flex justify-end gap-3 pt-4">
                          <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                            Sluiten
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-muted-foreground">
                          Door deze analyse te bevestigen, wordt deze beschikbaar voor andere tools
                          en kan deze niet meer worden bewerkt.
                        </p>

                        {confirmError && (
                          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                            <p className="text-sm text-destructive">{confirmError}</p>
                          </div>
                        )}

                        <div className="space-y-3">
                          <label className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              className="rounded mt-0.5"
                              checked={isContentChecked}
                              onChange={e => setIsContentChecked(e.target.checked)}
                            />
                            <span className="text-sm">
                              Ik heb de inhoud gecontroleerd en deze is klaar voor publicatie
                            </span>
                          </label>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                          <Button
                            variant="outline"
                            onClick={() => setShowConfirmDialog(false)}
                            disabled={isConfirming}
                          >
                            Annuleren
                          </Button>
                          <Button
                            variant="gradient"
                            onClick={confirmAnalysis}
                            loading={isConfirming}
                            disabled={confirmSuccess || !isContentChecked}
                          >
                            {isConfirming ? 'Bevestigen...' : 'Bevestigen'}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Sources Dialog */}
          <Dialog
            open={showSourcesDialog}
            onOpenChange={open => {
              setShowSourcesDialog(open)
              if (!open) {
                setActiveSentenceIndex(null) // Reset when closing
              }
            }}
          >
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Bronnen voor {selectedBlockForSources?.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {(() => {
                  // Get sources from scan object
                  let allScanSources: ScanSourceDetail[] = []
                  if (scan.sources) {
                    if (Array.isArray(scan.sources)) {
                      allScanSources = scan.sources
                    } else if (typeof scan.sources === 'object') {
                      allScanSources = Object.entries(scan.sources).map(
                        ([key, value]: [string, ScanSourceDetail]) => ({
                          id: key,
                          ...value,
                        })
                      )
                    }
                  }

                  // Get the original content for this block
                  const blockId = selectedBlockForSources?.id || ''
                  const originalContent =
                    originalBlockContent[blockId] || selectedBlockForSources?.content || ''

                  // Parse text into sentences with their sources
                  const sentences = parseSentencesWithSources(originalContent)

                  // Get sources for the active sentence if one is selected
                  const activeSources =
                    activeSentenceIndex !== null && sentences[activeSentenceIndex]
                      ? sentences[activeSentenceIndex].sourceIndices
                        .filter(idx => idx < allScanSources.length && allScanSources[idx])
                        .map(idx => ({ index: idx, source: allScanSources[idx] }))
                      : []

                  return (
                    <>
                      {/* Text with clickable source count badges */}
                      <div className="space-y-4">
                        <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                          {sentences.map((sentence, index) => (
                            <div key={index} className="leading-relaxed">
                              <span
                                className={`${activeSentenceIndex === index ? 'bg-orange-100 px-1 rounded' : ''}`}
                              >
                                {sentence.text}
                              </span>
                              {sentence.sourceIndices.length > 0 && (
                                <button
                                  onClick={() =>
                                    setActiveSentenceIndex(
                                      activeSentenceIndex === index ? null : index
                                    )
                                  }
                                  className={`ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded transition-all ${activeSentenceIndex === index
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                  title={`${sentence.sourceIndices.length} ${sentence.sourceIndices.length === 1 ? 'bron' : 'bronnen'}`}
                                >
                                  {sentence.sourceIndices.length}{' '}
                                  {sentence.sourceIndices.length === 1 ? 'bron' : 'bronnen'}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Sources list - only show when a sentence is active */}
                      {activeSentenceIndex !== null && activeSources.length > 0 && (
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold">
                            Bronnen voor geselecteerde zin ({activeSources.length})
                          </h3>
                          <div className="space-y-3">
                            {activeSources.map(({ index: originalIndex, source }, displayIndex) => {
                              const displayNumber = displayIndex + 1
                              const isExpanded = expandedSources.has(originalIndex)

                              const toggleExpanded = () => {
                                const newExpanded = new Set(expandedSources)
                                if (newExpanded.has(originalIndex)) {
                                  newExpanded.delete(originalIndex)
                                } else {
                                  newExpanded.add(originalIndex)
                                }
                                setExpandedSources(newExpanded)
                              }

                              return (
                                <div
                                  key={originalIndex}
                                  id={`source-${originalIndex}`}
                                  className="border rounded-lg overflow-hidden transition-all duration-300"
                                >
                                  {/* Collapsed Header - always visible */}
                                  <button
                                    onClick={toggleExpanded}
                                    className="w-full flex items-center gap-3 p-3 hover:bg-accent/50 transition-colors text-left"
                                  >
                                    <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                      {displayNumber}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                      {(() => {
                                        switch (source.sourceType?.toLowerCase()) {
                                          case 'facebook':
                                            return <MessageCircle className="w-4 h-4" />
                                          case 'twitter':
                                          case 'x':
                                            return <MessageCircle className="w-4 h-4" />
                                          case 'news':
                                            return <Newspaper className="w-4 h-4" />
                                          case 'reddit':
                                            return <MessageCircle className="w-4 h-4" />
                                          default:
                                            return <FileText className="w-4 h-4" />
                                        }
                                      })()}
                                      <span>{source.sourceType || 'other'}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium truncate">
                                        {(source.sourceType === 'news' || source.sourceType === 'blog')
                                          ? getDomainFromUrl(source.url || '') || source.name || source.title || 'Untitled'
                                          : source.title || source.author || source.name || 'Untitled'}
                                      </div>
                                      {source.author &&
                                        source.author !== (source.title || source.name) && (
                                          <div className="text-xs text-muted-foreground truncate">
                                            {source.author}
                                          </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {isExpanded ? (
                                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                      ) : (
                                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                      )}
                                    </div>
                                  </button>

                                  {/* Expanded Content */}
                                  {isExpanded && (
                                    <div className="px-3 pb-3 border-t bg-muted/20">
                                      <div className="pt-3 space-y-2">
                                        {source.sentiment && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">Sentiment:</span>
                                            <span
                                              className={`inline-block px-2 py-0.5 text-xs rounded ${source.sentiment === 'positive'
                                                ? 'bg-green-100 text-green-700'
                                                : source.sentiment === 'negative'
                                                  ? 'bg-red-100 text-red-700'
                                                  : 'bg-gray-100 text-gray-700'
                                                }`}
                                            >
                                              {source.sentiment === 'positive'
                                                ? 'Positief'
                                                : source.sentiment === 'negative'
                                                  ? 'Negatief'
                                                  : source.sentiment === 'neutral'
                                                    ? 'Neutraal'
                                                    : source.sentiment}
                                            </span>
                                          </div>
                                        )}
                                        {source.timestamp && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">Datum:</span>
                                            <span className="text-sm text-muted-foreground">
                                              {new Date(source.timestamp).toLocaleString('nl-NL')}
                                            </span>
                                          </div>
                                        )}
                                        {source.text && (
                                          <div className="space-y-1">
                                            <span className="text-sm font-medium">Inhoud:</span>
                                            <div className="text-sm text-muted-foreground bg-background p-2 rounded border">
                                              {source.text.length > 300
                                                ? source.text.substring(0, 300) + '...'
                                                : source.text}
                                            </div>
                                          </div>
                                        )}
                                        {source.url && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">Bron:</span>
                                            <a
                                              href={source.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 text-sm underline"
                                            >
                                              Bekijk bron →
                                            </a>
                                          </div>
                                        )}
                                        {source.count && source.count > 1 && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">
                                              Aantal berichten:
                                            </span>
                                            <span className="text-sm text-muted-foreground px-2 py-1 bg-background rounded border">
                                              {source.count}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {activeSentenceIndex === null && (
                        <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-6 text-center">
                          <div className="flex items-center justify-center gap-3 mb-2">
                            <MousePointer className="w-6 h-6" />
                            <Clipboard className="w-6 h-6" />
                          </div>
                          <p className="text-muted-foreground font-medium">
                            Klik op een blauwe bronbadge om de bijbehorende bronnen te bekijken
                          </p>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <DeleteScanDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            scan={scan}
            onConfirm={handleDeleteConfirm}
            isDeleting={isDeleting}
          />
        </>
      ) : null}
    </Layout>
  )
}
